import React from 'react';
import { Squares2X2Icon, MapPinIcon } from '../../Icons';

interface DashboardResultsHeaderProps {
    totalCount: number;
    viewMode: 'grid' | 'map';
    setViewMode: (mode: 'grid' | 'map') => void;
    searchMode: string;
    isLoading?: boolean;
    isSelectionMode?: boolean;
}

export const DashboardResultsHeader: React.FC<DashboardResultsHeaderProps> = ({
    totalCount,
    viewMode,
    setViewMode,
    searchMode,
    isLoading = false,
    isSelectionMode = false
}) => {
    // REGLA: Si el modo de búsqueda es por código o por mapa directo, o está en modo selección/compartir, no mostramos el selector de vista.
    if (searchMode === 'map' || searchMode === 'code' || isSelectionMode) return null;

    return (
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <style>{`
                @keyframes travel-line {
                    0% { stroke-dashoffset: 600; }
                    100% { stroke-dashoffset: 0; }
                }

                @keyframes shake-and-wait {
                    0%, 85%, 100% { transform: scale(1) rotate(0deg); }
                    86% { transform: scale(1.02) rotate(1deg); }
                    88% { transform: scale(1.02) rotate(-1.5deg); }
                    90% { transform: scale(1.02) rotate(1.5deg); }
                    92% { transform: scale(1.02) rotate(-1deg); }
                    94% { transform: scale(1.02) rotate(1deg); }
                    96% { transform: scale(1.02) rotate(-0.5deg); }
                    98% { transform: scale(1) rotate(0.5deg); }
                }

                .animate-glow-border {
                    animation: travel-line 4s linear infinite;
                    stroke-dasharray: 60 200;
                }

                .pulse-toggle-container {
                    animation: shake-and-wait 6s ease-in-out infinite;
                    box-shadow: 0 0 14px rgba(14, 165, 233, 0.25);
                    border: 1.5px solid rgba(14, 165, 233, 0.4);
                    transition: border-color 0.3s ease, box-shadow 0.3s ease;
                }
                
                .pulse-toggle-container:hover {
                    box-shadow: 0 0 18px rgba(14, 165, 233, 0.4);
                    border-color: rgba(14, 165, 233, 0.6);
                }
            `}</style>

            <span className="text-sm font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                RESULTADOS: {isLoading ? `${totalCount} (Contabilizando...)` : totalCount}
                {isLoading && (
                    <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
                )}
            </span>
            <div className="flex items-center bg-gray-100 p-1 rounded-xl relative pulse-toggle-container">
                {/* Outlining SVG border animation */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl overflow-hidden" fill="none">
                    <rect 
                        className="animate-glow-border" 
                        x="0.5" y="0.5" width="calc(100% - 1px)" height="calc(100% - 1px)" rx="11" 
                        stroke="url(#toggle-gradient-border)" strokeWidth="2"
                    />
                    <defs>
                        <linearGradient id="toggle-gradient-border" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#0ea5e9" />
                            <stop offset="50%" stopColor="#38bdf8" />
                            <stop offset="100%" stopColor="#0ea5e9" />
                        </linearGradient>
                    </defs>
                </svg>

                <button 
                    onClick={() => setViewMode('grid')} 
                    className={`px-4 py-2 rounded-lg transition-all flex items-center space-x-2 z-10 ${viewMode === 'grid' ? 'bg-white shadow-sm text-sky-600 font-extrabold' : 'text-gray-500 hover:text-gray-700 font-medium'}`}
                >
                    <Squares2X2Icon className="w-5 h-5"/>
                    <span className="hidden sm:inline text-xs font-bold uppercase">Ver lista</span>
                </button>
                <button 
                    onClick={() => setViewMode('map')} 
                    className={`px-4 py-2 rounded-lg transition-all flex items-center space-x-2 z-10 ${viewMode === 'map' ? 'bg-white shadow-sm text-sky-600 font-extrabold' : 'text-gray-500 hover:text-gray-700 font-medium'}`}
                >
                    <MapPinIcon className="w-5 h-5"/>
                    <span className="hidden sm:inline text-xs font-bold uppercase">Mostrar mapa</span>
                </button>
            </div>
        </div>
    );
};